//import { useState } from "react";

// HOOKS
import { useRef, useState } from "react";

// TYPES
import {
    IBPCheckin,
    ICancelSubscriptionResponse,
    IDetailedCheckin,
    IPPGCheckin,
    IReportDataResponse,
    IStripeSubInfoResponse,
    IStripeWebSubInfoResponse,
} from "./apiResponseTypes";

//import { setDefaultResultOrder } from "dns";
import { applyZoneToUtc } from "../../utils/dateTime";

// TODO - move types to api
export interface ICreateUserBodyPost {
    username: string;
    password: string;
}
export interface JWTDecoded {
    exp: number;
    iat: number;
    user_id: string;
}

export interface IBPLoggedCheckin {
    timestampUtc: string;
    systolicValue?: string;
    diastolicValue?: number;
    //timezoneUtcOffset?: string;
    userNote?: string;
    userTags: string[];
}

export interface IBPLoggedDataResponse {
    bpCheckins: IBPLoggedCheckin[];
}

interface ICreateUserResponseBody {
    access_token: string;
}

export interface IGetReportResponseItem {
    fromTimestampUtc: string;
    toTimestampUtc: string;
    reportId: string;
    reportType: string; // TODO enum
    secretKey: string; // might be blank?
    sharingEnabled: boolean;
}

// interface IEntitlementsStatusResponse {
//     account_status: string;
//     expiration_utc: string;
// }

const useIhrApi = (
    saveToken: (token: string) => void,
    saveEmail: (userEmail: string) => void,
    saveIsPremium: (userIsPremium: boolean) => void,
    savePremiumFromStripe: (premiumFromStripe: boolean) => void,
    saveActiveSubscription: (activeStripeSub: IStripeSubInfoResponse) => void,
    clearLocalSubscriptionStorage: () => void,
) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    //const [email, setEmail] = useState<string>(""); // TODO - this should prob come from the server along with other metadata (e.g. eg status)
    const [hrCheckIns, setHRCheckIns] = useState<IPPGCheckin[]>([]);
    const [bpCheckIns, setBPCheckIns] = useState<IBPCheckin[]>([]);
    const [detailedCheckins, setDetailedCheckins] = useState<
        IDetailedCheckin[]
    >([]);
    const [reports, setReports] = useState<IGetReportResponseItem[]>([]);
    const [reportNotFound, setReportNotFound] = useState<boolean | null>(null);
    const [reportsNotFound, setReportsNotFound] = useState<boolean | null>(
        null,
    );

    const [fromTimestampUtc, setFromTimestampUtc] = useState<null | string>(
        null,
    );
    const [toTimestampUtc, setToTimestampUtc] = useState<null | string>(null);
    const [createdAtTimestampUtc, setCreatedAtTimestampUtc] = useState<
        null | string
    >(null);

    // this is now a misnomer and not derived from getEntitlements which is deprecated.
    // it is actually coming from web/subscriptions/info
    const [entitlementResolved, setEntitlementResolved] = useState(false);

    // certainly not the best way to handle this - TODO optimize / make generic when there is down time
    const [creatingUserThatAlreadyExists, setCreatingUserThatAlreadyExists] =
        useState<boolean | null>(null);

    // this determines if the user is able to purchase premium
    const [eligibility, setEligibility] = useState<boolean | null>(null);

    // this determines if a user has an active subscription and is distinct from eligibility
    // const [activeSubscription, setActiveUserSubscription] =
    //     useState<IStripeSubInfoResponse | null>(null);

    const [maskedEmailAddress, setMaskedEmailAddress] = useState<string>("");

    const lastEvaluatedHRTimestampUtc = useRef<string | null>(null);
    const lastEvaluatedHRCheckInId = useRef<string | null>(null);
    const lastEvaluatedBPTimestampUtc = useRef<string | null>(null);
    const lastEvaluatedBPCheckInId = useRef<string | null>(null);
    const lastEvaluatedDetailedCheckinId = useRef<string | null>(null);
    const lastEvaluatedDetailedCheckinTimestampUtc = useRef<string | null>(
        null,
    );

    const wrapWithLoading = async <T>(fn: () => Promise<T>): Promise<T> => {
        //console.log("wrapWithLoading - set true");
        setIsLoading(true);
        try {
            return await fn();
        } finally {
            //console.log("wrapWithLoading - set false");
            setIsLoading(false);
        }
    };

    const postLoginUser = (email: string, password: string): Promise<void> => {
        setEntitlementResolved(false);
        return wrapWithLoading(async () => {
            //try {
            const headers = new Headers();
            headers.append("x-api-key", process.env.REACT_APP_API_KEY!);
            headers.append("Content-Type", "application/json");

            const body = JSON.stringify({ username: email, password });

            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/${process.env.REACT_APP_ENV}/web/auth/login`,
                {
                    method: "POST",
                    headers,
                    body,
                },
            );

            if (!response.ok) {
                // TODO better error handling
                throw new Error(`Request failed with status ${response}`);
            }

            const result: ICreateUserResponseBody = await response.json();
            //console.log(result);
            saveToken(result.access_token);
            saveEmail(email); // Local storage managed by the auth hook
            await getStripeSubscriptionInfo(result.access_token);
            // await getEntitlement(result.access_token);
            // } catch (e) {
            //     console.error(`Request failed with status ${e}`);
            // }
        });
    };

    const getReports = (accessToken: string): Promise<void> => {
        setReportNotFound(null);
        setReportsNotFound(null);
        return wrapWithLoading(async () => {
            try {
                const headers = new Headers();
                headers.append("x-api-key", process.env.REACT_APP_API_KEY!);
                headers.append("Content-Type", "application/json");
                headers.append("Authorization", accessToken);

                const response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/${process.env.REACT_APP_ENV}/web/reports`,
                    {
                        method: "GET",
                        headers,
                    },
                );

                if (!response.ok) {
                    setReportsNotFound(true);
                    // TODO better error handling
                    //console.error(`Request failed with status ${response}`);
                    throw new Error(`Request failed with status ${response}`);
                }

                const reps: IGetReportResponseItem[] = await response.json();
                //console.log(reps);
                setReports(reps);
                setReportsNotFound(false);
            } catch (e) {
                console.error(`Request failed with status ${e}`);
            }
        });
    };

    const getReport = (
        reportId: string,
        secretKey: string = "",
        recurse: boolean = false,
    ): Promise<void> => {
        setHRCheckIns([]);
        setBPCheckIns([]);
        setDetailedCheckins([]);
        setReportNotFound(null);
        lastEvaluatedHRTimestampUtc.current = null;
        lastEvaluatedBPTimestampUtc.current = null;
        lastEvaluatedBPCheckInId.current = null;
        lastEvaluatedBPTimestampUtc.current = null;
        lastEvaluatedDetailedCheckinId.current = null;
        lastEvaluatedDetailedCheckinTimestampUtc.current = null;
        setFromTimestampUtc(null);
        setToTimestampUtc(null);
        setCreatedAtTimestampUtc(null);
        return wrapWithLoading(async () => {
            try {
                const allHRCheckIns: IPPGCheckin[] = [];
                const allBPCheckIns: IBPCheckin[] = [];
                const allDetailedCheckIns: IDetailedCheckin[] = [];

                let hrBottomedOut = false;
                let bpBottomedOut = false;
                let detailedBottomedOut = false;

                let iterationCount = 0;
                const MAX_ITERATIONS = 50;

                while (iterationCount++ < MAX_ITERATIONS) {
                    //&& runs < 3
                    //runs++;
                    const headers = new Headers();
                    headers.append("x-api-key", process.env.REACT_APP_API_KEY!);
                    headers.append("Content-Type", "application/json");
                    const prevDetailedId =
                        lastEvaluatedDetailedCheckinId.current;

                    /*
                        rid = cd93c9323f44495da33a4394a34748ca
                        hr_ts = 2025-04-21T23:08:15.497
                        hr_cid = 2456607890
                        bp_ts = 2025-05-13T18:18:00.000
                        bp_cid = 51947C7A4187482B9F373CB4BA0CF840
                    */

                    const rawParams = {
                        hr_ts: "",
                        hr_cid: "",
                        bp_ts: "",
                        bp_cid: "",
                        d_ts: "",
                        d_cid: "",
                        rid: reportId,
                        sk: secretKey ?? "",
                    };

                    if (!hrBottomedOut) {
                        rawParams.hr_ts =
                            lastEvaluatedHRTimestampUtc.current ?? "";
                        rawParams.hr_cid =
                            lastEvaluatedHRCheckInId.current ?? "";
                    }

                    if (!bpBottomedOut) {
                        rawParams.bp_ts =
                            lastEvaluatedBPTimestampUtc.current ?? "";
                        rawParams.bp_cid =
                            lastEvaluatedBPCheckInId.current ?? "";
                    }

                    if (!detailedBottomedOut) {
                        rawParams.d_ts =
                            lastEvaluatedDetailedCheckinTimestampUtc.current ??
                            "";
                        rawParams.d_cid =
                            lastEvaluatedDetailedCheckinId.current ?? "";
                    }

                    const params = Object.entries(rawParams)
                        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
                        .join("&");

                    const url = `${process.env.REACT_APP_API_BASE_URL}/${process.env.REACT_APP_ENV}/web/reports/data?${params}`;
                    const response = await fetch(url, {
                        method: "GET",
                        headers,
                    });

                    if (!response.ok) {
                        setReportNotFound(true);
                        throw new Error(
                            `Request for reports failed with status ${response}`,
                        );
                        // if (response.status === 404) {
                        //     return;
                        // } else {
                        //     console.error(
                        //         `Request failed with status ${response.status}`,
                        //     );
                        //     return;
                        // }
                    }

                    const res: IReportDataResponse = await response.json();
                    //console.log(res);
                    const {
                        hr_ppg_data,
                        bp_logged_data,
                        detailed_ppg_data,
                        from_timestamp_utc,
                        to_timestamp_utc,
                        created_timestamp_utc,
                    } = res;
                    const {
                        hr_checkins,
                        last_evaluated_hr_timestamp_utc,
                        last_evaluated_hr_checkin_id,
                    } = hr_ppg_data;
                    const {
                        bp_checkins,
                        last_evaluated_bp_timestamp_utc,
                        last_evaluated_bp_checkin_id,
                    } = bp_logged_data;
                    const {
                        detailed_checkins,
                        last_evaluated_detailed_timestamp_utc,
                        last_evaluated_detailed_checkin_id,
                    } = detailed_ppg_data;

                    /*
                        checkins,
                        last_evaluated_checkin_id,
                        last_evaluated_timestamp_utc,
                        from_timestamp_utc,
                        to_timestamp_utc,
                        created_timestamp_utc,
                    */

                    // console.log(checkins);
                    // console.log(last_evaluated_checkin_id);
                    // console.log(last_evaluated_timestamp_utc);
                    // console.log("RES", res);
                    setFromTimestampUtc(from_timestamp_utc);
                    setToTimestampUtc(to_timestamp_utc);
                    setCreatedAtTimestampUtc(created_timestamp_utc);

                    if (
                        !hr_checkins.length &&
                        !bp_checkins.length &&
                        !detailed_checkins.length
                    )
                        break;
                    //console.log(res);

                    if (!hrBottomedOut) {
                        allHRCheckIns.push(...hr_checkins);
                    }
                    if (!bpBottomedOut) {
                        allBPCheckIns.push(...bp_checkins);
                    }
                    if (!detailedBottomedOut) {
                        allDetailedCheckIns.push(...detailed_checkins);
                    }

                    lastEvaluatedHRTimestampUtc.current =
                        last_evaluated_hr_timestamp_utc ?? null;
                    lastEvaluatedHRCheckInId.current =
                        last_evaluated_hr_checkin_id ?? null;
                    lastEvaluatedBPTimestampUtc.current =
                        last_evaluated_bp_timestamp_utc ?? null;
                    lastEvaluatedBPCheckInId.current =
                        last_evaluated_bp_checkin_id ?? null;
                    lastEvaluatedDetailedCheckinId.current =
                        last_evaluated_detailed_checkin_id ?? null;
                    lastEvaluatedDetailedCheckinTimestampUtc.current =
                        last_evaluated_detailed_timestamp_utc ?? null;

                    if (
                        !last_evaluated_hr_timestamp_utc ||
                        !last_evaluated_hr_checkin_id
                    ) {
                        hrBottomedOut = true;
                    }

                    if (
                        !last_evaluated_bp_timestamp_utc ||
                        !last_evaluated_bp_checkin_id
                    ) {
                        bpBottomedOut = true;
                    }

                    if (
                        !last_evaluated_detailed_timestamp_utc ||
                        !last_evaluated_detailed_checkin_id ||
                        last_evaluated_detailed_checkin_id === prevDetailedId
                    ) {
                        detailedBottomedOut = true;
                    }

                    // const shortCircuit =
                    //     lastEvaluatedHRTimestampUtc.current === null ||
                    //     lastEvaluatedHRCheckInId.current === null ||
                    //     lastEvaluatedBPTimestampUtc.current === null ||
                    //     lastEvaluatedBPCheckInId.current === null;

                    if (
                        !recurse ||
                        (hrBottomedOut && bpBottomedOut && detailedBottomedOut)
                    )
                        break;
                }

                const HRCheckinsWithTimestamps = [
                    ...allHRCheckIns,
                    ...(allDetailedCheckIns.map((x) => {
                        return { ...x, value: x.valueHeartRateBpm };
                    }) as IPPGCheckin[]), // TODO - separate when appropriate
                ].map((x) => {
                    return {
                        ...x,
                        checkinDateTimeStamp: applyZoneToUtc(
                            x.timestampUtc,
                            String(x.timezoneUtcOffset),
                        ),
                    };
                });

                const BPCheckinsWithTimestamps = allBPCheckIns.map((x) => {
                    return {
                        ...x,
                        checkinDateTimeStamp: applyZoneToUtc(
                            x.timestampUtc,
                            String(x.timezoneUtcOffset),
                        ),
                    };
                });

                setHRCheckIns(HRCheckinsWithTimestamps);
                setBPCheckIns(BPCheckinsWithTimestamps);
                //setDetailedCheckins(allDetailedCheckIns); // may not need this after all
                setReportNotFound(false);
            } catch (e) {
                console.error("error getting report", e);
            }
        });
    };

    type EligibilityResponse = {
    eligible?: unknown;
    offerExpiration?: unknown;
    };

    type EligibilityResult = {
    eligible: boolean;
    offerExpiration?: string; // ISO millis string if present + valid
    };

    const ISO_MILLIS_RE =
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})\.(\d{3})$/;

    const isValidIsoMillis = (ts: string): boolean => {
    const m = ISO_MILLIS_RE.exec(ts);
    if (!m) return false;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    const h = Number(m[4]);
    const mi = Number(m[5]);
    const s = Number(m[6]);
    const ms = Number(m[7]);
    const dt = new Date(Date.UTC(y, mo - 1, d, h, mi, s, ms));
    return !Number.isNaN(dt.getTime());
    };

    const fetchEligibility = async (
    userId: string,
    offerType?: string,
    opts?: { signal?: AbortSignal },
    ): Promise<EligibilityResult> => {
    const base = process.env.REACT_APP_API_BASE_URL!;
    const env = process.env.REACT_APP_ENV!;
    const url = `${base}/${env}/web/subscriptions/eligibility`;

    const body: { u: string; offerType?: string } = { u: userId };
    if (offerType?.trim()) body.offerType = offerType.trim();

    try {
        const res = await fetch(url, {
        method: "POST",
        headers: {
            "x-api-key": process.env.REACT_APP_API_KEY as string,
            "Content-Type": "application/json",
            Accept: "application/json",
        },
        body: JSON.stringify(body),
        signal: opts?.signal,
        });

        if (!res.ok) {
        const text = await res.text().catch(() => "");
        console.error("Eligibility request failed", res.status, text);
        setEligibility(false);
        return { eligible: false };
        }

        const data = (await res.json()) as EligibilityResponse;

        const eligible =
        typeof data?.eligible === "boolean" ? (data.eligible as boolean) : false;

        const offerExpiration =
        typeof data?.offerExpiration === "string" && isValidIsoMillis(data.offerExpiration)
            ? data.offerExpiration
            : undefined;

        setEligibility(eligible);

        return { eligible, offerExpiration };
    } catch (err) {
        if ((err as any)?.name !== "AbortError") {
        console.error("Eligibility request error", err);
        }
        setEligibility(false);
        return { eligible: false };
    }
    };


    const postSubscriptionsCreate = async (
        uid: string,
        priceId: string, // need this do differentiate between different landers
        paymentMethodId: string, // need this to di different landers
        onSuccess: () => void,
        onFail: (error?: string) => void,
    ): Promise<void> => {
        const base = process.env.REACT_APP_API_BASE_URL!;
        const env = process.env.REACT_APP_ENV!;
        const url = `${base}/${env}/web/subscriptions/create`;

        // const myHeaders = new Headers();
        // myHeaders.append("x-api-key", "3327692560");
        // myHeaders.append("Content-Type", "application/json");

        // const requestOptions = {
        //     method: "POST",
        //     headers: myHeaders,
        //     body: raw,
        //     redirect: "follow",
        // };

        try {
            const res = await fetch(url, {
                method: "POST",
                headers: {
                    "x-api-key": process.env.REACT_APP_API_KEY as string, // REQUIRED
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({
                    uid: uid,
                    pid: priceId,
                    pmid: paymentMethodId,
                    pp: "stripe",
                }),
            });

            const result = await res.json();

            if (!res.ok) {
                // const text = await res.text().catch(() => "");
                // console.error("Premium grant failed", res.status, text);
                if (result.error === "user is not eligible") {
                    onFail(result.error);
                }
            }

            const { success, masked_email_address } = result;

            if (res.status === 200) {
                // redirect to success page
                setMaskedEmailAddress(masked_email_address);
                onSuccess();
            } else if (res.status === 400) {
            }
        } catch (err) {
            onFail();
            if ((err as any)?.name !== "AbortError") {
                console.error("Premium request error", err);
            }
        }
    };

    const postForgotPasswordAndSendCode = (
        email: string,
        onSuccess: () => void,
        onFail: (error: string) => void,
    ): Promise<void> => {
        //setEntitlementResolved(false);
        return wrapWithLoading(async () => {
            //try {
            const headers = new Headers();
            headers.append("x-api-key", process.env.REACT_APP_API_KEY!);
            headers.append("Content-Type", "application/json");

            const body = JSON.stringify({
                username: email,
                is_dev: process.env.REACT_APP_ENV === "staging",
            });

            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/${process.env.REACT_APP_ENV}/web/auth/forgot-password`,
                {
                    method: "POST",
                    headers,
                    body,
                },
            );
            const result: ICreateUserResponseBody = await response.json();
            //console.log("RESRES", response, result);

            if (!response.ok) {
                // TODO better error handling
                if (
                    (result as any).error ===
                    "User initiated migration needed in v8+ client"
                ) {
                    onFail("migration");
                } else {
                    onFail("unknown");
                }
            } else {
                if ((result as any).error) {
                    onFail((response as any).error);
                } else {
                    onSuccess();
                }
            }

            // } catch (e) {
            //     console.error(`Request failed with status ${e}`);
            // }
        });
    };

    /**
     * username is the same as email??
     */
    const postCodeAndResetPassword = (
        username: string,
        code: string,
        newPassword: string,
        onSuccess: () => void,
        onFail: (error: string) => void,
    ): Promise<void> => {
        //setEntitlementResolved(false);
        return wrapWithLoading(async () => {
            //try {
            const headers = new Headers();
            headers.append("x-api-key", process.env.REACT_APP_API_KEY!);
            headers.append("Content-Type", "application/json");

            const body = JSON.stringify({
                username,
                code,
                new_password: newPassword,
            });

            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/${process.env.REACT_APP_ENV}/web/auth/reset-password`,
                {
                    method: "POST",
                    headers,
                    body,
                },
            );
            const result: ICreateUserResponseBody = await response.json();
            if (!response.ok) {
                // TODO better error handling
                //throw new Error(`Request failed with status ${response}`);
                onFail((result as any).error as string);
            } else {
                onSuccess();
            }

            // } catch (e) {
            //     console.error(`Request failed with status ${e}`);
            // }
        });
    };

    const webUnsub = (
        email: string,
        unsubscribe_topics: string[],
        onSuccess: () => void,
        onFail: (error: string) => void,
    ): Promise<void> => {
        //setEntitlementResolved(false);
        return wrapWithLoading(async () => {
            //try {
            const headers = new Headers();
            headers.append("x-api-key", process.env.REACT_APP_API_KEY!);
            headers.append("Content-Type", "application/json");

            const body = JSON.stringify({
                email,
                unsubscribe_topics,
            });

            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/${process.env.REACT_APP_ENV}/web/user/unsubscribe`,
                {
                    method: "POST",
                    headers,
                    body,
                },
            );
            const result: ICreateUserResponseBody = await response.json();
            if (!response.ok) {
                // TODO better error handling
                onFail((result as any).error as string);
            } else {
                onSuccess();
            }
        });
    };

    const webVerifyAccount = (
        email: string,
        onSuccess: () => void,
        onFail: (error: string) => void,
    ): Promise<void> => {
        //setEntitlementResolved(false);
        return wrapWithLoading(async () => {
            //try {
            const headers = new Headers();
            headers.append("x-api-key", process.env.REACT_APP_API_KEY!);
            headers.append("Content-Type", "application/json");

            const body = JSON.stringify({
                email
            });

            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/${process.env.REACT_APP_ENV}/web/user/verify`,
                {
                    method: "POST",
                    headers,
                    body,
                },
            );
            const result: ICreateUserResponseBody = await response.json();
            if (!response.ok) {
                // TODO better error handling
                onFail((result as any).error as string);
            } else {
                onSuccess();
            }
        });
    };

    const postCancelSubscription = (
        accessToken: string,
        subscriptionId: string,
        onSuccess?: () => void,
        onFail?: (error: string) => void,
    ): Promise<void> => {
        //setEntitlementResolved(false);
        return wrapWithLoading(async () => {
            //try {
            const headers = new Headers();
            headers.append("Authorization", accessToken);
            headers.append("x-api-key", process.env.REACT_APP_API_KEY!);
            headers.append("Content-Type", "application/json");

            const body = JSON.stringify({
                platform: "stripe",
                subscription_id: subscriptionId,
            });

            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/${process.env.REACT_APP_ENV}/web/subscriptions/cancel`,
                {
                    method: "POST",
                    headers,
                    body,
                },
            );

            if (!response.ok) {
                // TODO better error handling
                if (onFail) onFail("API");

                //throw new Error(`Request failed with status ${response}`);
                //onFail((response as any).message as string);
            } else {
                const result: ICancelSubscriptionResponse =
                    await response.json();
                if (result.cancellation_success) {
                    if (onSuccess) onSuccess();
                } else {
                    if (onFail) onFail("API");
                }
            }

            // } catch (e) {
            //     console.error(`Request failed with status ${e}`);
            // }
        });
    };

    /**
     * This should be called before rendering any page which contains subscription info, and after any API
     * call is made that could alter subscription info in order to keep the back end in sync with the client.
     * So if the user cancels their strip subscription via the manage-subscription endpoint, then after a successful
     * call to the manage endpoint this should be chained.
     **/
    const getStripeSubscriptionInfo = async (
        accessToken: string,
        onSuccess?: () => void,
        onFail?: (error: string) => void,
    ) => {
        return wrapWithLoading(async () => {
            //try {
            const headers = new Headers();
            headers.append("Authorization", accessToken);
            headers.append("x-api-key", process.env.REACT_APP_API_KEY!);
            headers.append("Content-Type", "application/json");
            const url = `${process.env.REACT_APP_API_BASE_URL}/${process.env.REACT_APP_ENV}/web/subscriptions/info`;
            const response = await fetch(url, {
                method: "GET",
                headers,
            });

            if (!response.ok) {
                // TODO better error handling
                if (onFail) {
                    onFail(JSON.stringify(response));
                    if (process.env.REACT_APP_ENV === "staging") {
                        console.log(
                            "\failure to get response when getting subscription:",
                            response,
                        );
                    }
                }
                //throw new Error(`Request failed with status ${response}`);
            } else {
                const result: IStripeWebSubInfoResponse = await response.json();

                const userIsPremium = result?.account_status === "premium";
                const premiumFromStripe = result?.payment_platform === "stripe";
                //const premium_expiration = result?.expiration_utc;

                saveIsPremium(userIsPremium);
                savePremiumFromStripe(premiumFromStripe);

                /**
                 * 1. If the user is premium and the premium is from stripe
                 *          -> there must be either an active stripe subscription, or the subscription is cancelled, but not expired
                 * 2. If the user is premium and the premium is not from stripe
                 *          -> we don't care about the subs if any even exist
                 * 3. If the user is not premium we don't care about subs at all
                 */

                if (process.env.REACT_APP_ENV === "staging") {
                    console.log("\tpremiumFromStripe:", result);
                }

                if (userIsPremium && premiumFromStripe) {
                    // This will either be an active sub, or an unexpired + cancelled sub
                    const stripeSubProvidingPremium =
                        result.stripe_sub_info?.find((sub) => {
                            return (
                                sub.status === "active" ||
                                (sub.status === "canceled" &&
                                    Date.now() <
                                        new Date(sub.date_utc).getTime())
                            );
                        });
                    if (stripeSubProvidingPremium !== undefined) {
                        // console.log(
                        //     "Saving active sub",
                        //     stripeSubProvidingPremium,
                        // );
                        // console.log(
                        //     "stripe sub prov prem",
                        //     stripeSubProvidingPremium,
                        // );
                        clearLocalSubscriptionStorage(); // not an ideal way of doing this - wish the API could return the relevant info after a cancel...
                        saveActiveSubscription(stripeSubProvidingPremium);
                        saveIsPremium(userIsPremium);
                        savePremiumFromStripe(premiumFromStripe);
                    } else {
                        if (process.env.REACT_APP_ENV === "staging") {
                            console.warn(
                                "Detected premium from strip W/O active / unexpired eg",
                            );
                        }
                    }
                }
                if (onSuccess) onSuccess();
                setEntitlementResolved(true);

                // if (userIsPremium) {
                //     setActiveUserSubscription(activeSub);
                //     saveIsPremium(true);
                //     setEntitlementResolved(true);
                // } else {
                //     saveIsPremium(false);
                //     setEntitlementResolved(true);
                // }
            }

            // } catch (e) {
            //     console.error(`Request failed with status ${e}`);
            // }
        });
    };

    return {
        isLoading,
        reports,
        hrCheckIns,
        bpCheckIns,
        detailedCheckins,
        reportNotFound,
        reportsNotFound,
        creatingUserThatAlreadyExists,
        fromTimestampUtc, // spec added at the last minute
        toTimestampUtc, // spec added at the last minute
        createdAtTimestampUtc, // spec added at the last minute
        entitlementResolved,
        eligibility,
        maskedEmailAddress,
        webUnsub,
        webVerifyAccount,
        postSubscriptionsCreate,
        fetchEligibility,
        postLoginUser,
        getReport,
        getReports,
        getStripeSubscriptionInfo,
        postCodeAndResetPassword,
        postForgotPasswordAndSendCode,
        postCancelSubscription,
        //activeSubscription, ---- DO NOT USE THIS. Don't even track this here. Keep it on useAuthToken
        //postCreateUser, --- no longer part of mvp
        //getEntitlement, -- deprecated
        // getReportFromReportsApiCall, -- not part of MVP
    };
};

export default useIhrApi;

/**
 * NOTE - no longer part of MVP
 */
// const postCreateUser = (email: string, password: string): Promise<void> => {
//     setCreatingUserThatAlreadyExists(null);
//     setEntitlementResolved(false);
//     return wrapWithLoading(async () => {
//         try {
//             const headers = new Headers();
//             headers.append("x-api-key", process.env.REACT_APP_API_KEY!);
//             headers.append("Content-Type", "application/json");

//             const body = JSON.stringify({ username: email, password });

//             const response = await fetch(
//                 `${process.env.REACT_APP_API_BASE_URL}/${process.env.REACT_APP_ENV}/web/auth/create-user`,
//                 {
//                     method: "POST",
//                     headers,
//                     body,
//                 },
//             );

//             // TODO - add error to response body
//             const result: ICreateUserResponseBody = await response.json();
//             if (!response.ok) {
//                 // TODO this is not a good way of handling this... maybe use a 409 instead
//                 if (
//                     (result as any)?.error.trim().toLowerCase() ===
//                     "User already exists".toLowerCase()
//                 ) {
//                     setCreatingUserThatAlreadyExists(true);
//                 }

//                 // console.error(
//                 //     //setCreatingUserThatAlreadyExists(true),
//                 //     `Request failed with status ${response.status} --- ${
//                 //         (result as any).error
//                 //     }`,
//                 // );
//                 throw new Error(
//                     `Request failed with status ${response.status}`,
//                 );
//             }

//             saveToken(result.access_token);
//             saveEmail(email);
//             setCreatingUserThatAlreadyExists(false);
//             //await getEntitlement(result.access_token);
//             await getStripeSubscriptionInfo(result.access_token);
//         } catch (e) {
//             console.error(`Create user request failed: ${e}`);
//         }
//     });
// };

/**
 * DEPRECATED - this is determined with web/subscriptions/info
 */
// const getEntitlement = (accessToken: string): Promise<void> => {
//     return wrapWithLoading(async () => {
//         try {
//             const headers = new Headers();
//             headers.append("x-api-key", process.env.REACT_APP_API_KEY!);
//             headers.append("Content-Type", "application/json");
//             headers.append("Authorization", accessToken);

//             const response = await fetch(
//                 `${process.env.REACT_APP_API_BASE_URL}/${process.env.REACT_APP_ENV}/web/entitlements/status`,
//                 {
//                     method: "GET",
//                     headers,
//                 },
//             );

//             if (!response.ok) {
//                 throw new Error(
//                     `Entitlement request failed with status ${response}`,
//                 );
//             }

//             const resp: IEntitlementsStatusResponse = await response.json();
//             const { account_status /* expiration_utc */ } = resp;
//             // console.log(
//             //     "account_status, expiration_utc",
//             //     account_status,
//             //     expiration_utc,
//             //     account_status.trim() === "premium",
//             // );
//             saveIsPremium(account_status.trim() === "premium");
//             setEntitlementResolved(true);
//         } catch (e) {
//             console.error(`Entitlements request failed with status ${e}`);
//         }
//     });
// };

// NOTE --- keeping only because this has the ability to bundle report and checking APIs
// this will probably never be useful, but here for reference
// /**
//  * NOTE - THIS IS ONLY FOR USE BY AUTHENTICATED USERS!!!
//  * it bundles GET reports and GET checkins so that authenticated
//  * users can view reports that are marked not shareable
//  * @param token
//  * @param reportId
//  * @returns
//  */
// const getReportFromReportsApiCall = async (
//     token: string,
//     reportId: string,
// ): Promise<void> => {
//     setReportsNotFound(null);
//     setReportNotFound(null);
//     setReports([]);
//     setCheckIns([]);
//     console.log(`getReportFromReportsApiCall ${token} ${reportId}`);
//     return wrapWithLoading(async () => {
//         try {
//             const headers = new Headers();
//             headers.append("x-api-key", process.env.REACT_APP_API_KEY!);
//             headers.append("Content-Type", "application/json");
//             headers.append("Authorization", token);

//             const reportsResponse = await fetch(
//                 `${process.env.REACT_APP_API_BASE_URL}/${process.env.REACT_APP_ENV}/web/reports`,
//                 {
//                     method: "GET",
//                     headers,
//                 },
//             );

//             if (!reportsResponse.ok) {
//                 setReportsNotFound(true); // show 404
//                 setReportNotFound(true);
//                 // TODO better error handling
//                 // console.error(
//                 //     `Failed to fetch reports: ${reportsResponse.status}`,
//                 // );
//                 throw new Error(
//                     `Failed to fetch reports: ${reportsResponse.status}`,
//                 );
//             }

//             const reportList: IGetReportResponseItem[] =
//                 await reportsResponse.json();
//             console.log("reports list", reportList);
//             setReports(reportList);
//             setReportsNotFound(false);

//             const matchedReport = reportList.find(
//                 (r) => r.reportId === reportId,
//             );

//             if (!matchedReport) {
//                 setReportNotFound(true);
//                 // 404
//                 // TODO better error handling
//                 throw new Error(`Report with ID ${reportId} not found`);
//                 //console.error(`Report with ID ${reportId} not found`);
//             }

//             // now fetch specific report
//             const dataParams = new URLSearchParams({
//                 rid: reportId,
//                 ts: lastEvaluatedTimestampUtc.current ?? "",
//                 cid: lastEvaluatedCheckInId.current ?? "",
//                 sk: matchedReport?.secretKey ?? "",
//             });

//             const reportDataResponse = await fetch(
//                 `${process.env.REACT_APP_API_BASE_URL}/${process.env.REACT_APP_ENV}/web/reports/data?${dataParams}`,
//                 {
//                     method: "GET",
//                     headers: new Headers({
//                         "x-api-key": process.env.REACT_APP_API_KEY!,
//                         "Content-Type": "application/json",
//                     }),
//                 },
//             );

//             if (!reportDataResponse.ok) {
//                 setReportNotFound(true);
//                 // TODO better error handling
//                 // if (reportDataResponse.status === 404) {
//                 //     setReportNotFound(true);
//                 // }
//                 // console.error(
//                 //     `Failed to fetch report data: ${reportDataResponse.status}`,
//                 // );
//                 throw new Error(
//                     `Failed to fetch report data: ${reportDataResponse.status}`,
//                 );
//             }

//             const {
//                 checkins,
//                 last_evaluated_checkin_id,
//                 last_evaluated_timestamp_utc,
//             }: IGetReportsResponse = await reportDataResponse.json();

//             setCheckIns(checkins);
//             setReportNotFound(false);
//             lastEvaluatedCheckInId.current = last_evaluated_checkin_id;
//             lastEvaluatedTimestampUtc.current =
//                 last_evaluated_timestamp_utc;
//         } catch (e) {
//             console.error("Failed to fetch report", e);
//         }
//     });
// };
